import { useState } from 'react';
import { getUniqueItems } from 'src/common/config/FunctionHelperPartTwo';

interface UseChipInputOptions {
  /** Initial list of chips (e.g. from API data). */
  initialList?: string[];
  /** Maximum number of chips allowed. */
  maxLimit?: number;
  /** Label used in error messages — e.g. "competitors" or "topics". */
  itemLabel?: string;
}

export interface ChipInputError {
  open: boolean;
  message: string;
}

/**
 * Encapsulates a comma-separated chip/tag input pattern.
 *
 * Manages input text, chip list, and validation errors with handlers for
 * change, keydown (Enter / Backspace), blur, and delete.
 */
export const useChipInput = ({
  initialList = [],
  maxLimit = 4,
  itemLabel = 'competitors'
}: UseChipInputOptions = {}) => {
  const [input, setInput] = useState('');
  const [list, setList] = useState<string[]>(initialList);
  const [error, setError] = useState<ChipInputError | null>(null);

  const itemLabelCapitalized =
    itemLabel === 'topics' ? 'Topic' : 'Competitor name';

  // ── Change handler ──
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Step 1: Normalize input — convert newlines (\n, \r) to commas
    const rawValue = e.target.value.replace(/[\n\r]+/g, ',');

    // Step 2: Remove leading spaces for cleaner typing
    const value = rawValue.trimStart();

    // Step 3: Update input field state (used as controlled input)
    setInput(value);

    // Step 4: If already at max allowed keywords and user is still typing, show error
    if (list?.length >= maxLimit && value.length > 0) {
      setError({
        open: true,
        message: `Only ${maxLimit} ${itemLabel} allowed`
      });
      return;
    }

    // Check single input length
    if (!value?.includes(',') && value?.length > 50) {
      setError({
        open: true,
        message: `${itemLabelCapitalized} cannot exceed 50 characters`
      });
    }

    // Step 5: Check if the current value contains commas (indicating keyword entry)
    if (value?.includes(',')) {
      // Split the string into parts using commas
      const parts = value.split(',');

      // Keep the last part as the residual input (likely unfinished typing)
      const lastPart = parts.pop()?.trim() ?? '';

      // Clean each keyword, remove empties
      const newKeywords = parts
        ?.map((kw) => kw?.trim())
        ?.filter((kw) => kw?.length > 0);

      if (newKeywords?.some((kw) => kw?.length > 50)) {
        setError({
          open: true,
          message: `${itemLabelCapitalized} cannot exceed 50 characters`
        });
        return;
      }

      // Combine new keywords with already existing ones
      const combined = [...list, ...newKeywords];

      // Ensure uniqueness and respect max limit
      const uniqueKeywords = getUniqueItems(combined).slice(0, maxLimit);

      // Step 6: If too many keywords, trim and show error
      if (uniqueKeywords.length > maxLimit) {
        setList(uniqueKeywords);

        setInput('');
        setError({
          open: true,
          message: `Only ${maxLimit} ${itemLabel} allowed`
        });
        return;
      }

      // Step 7: Update state with accepted keywords
      setList(uniqueKeywords);

      // Step 8: Set the leftover input in the field (e.g., unfinished keyword)
      setInput(lastPart);

      // Step 9: Reset any existing error
      if (lastPart?.length > 50) {
        setError({
          open: true,
          message: `${itemLabelCapitalized} cannot exceed 50 characters`
        });
      } else {
        setError({ open: false, message: '' });
      }
    } else if (value?.trim() === '') {
      // Step 10: If field is cleared, reset error state
      setError({ open: false, message: '' });
    } else if (value?.length <= 50) {
      setError({ open: false, message: '' });
    }
  };

  // ── Key handler ──
  const handleKeyDown = (e: React.KeyboardEvent<any>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent newline
      if (input?.trim()) {
        // Reuse the comma logic by appending a comma
        handleInputChange({
          target: { value: input + ',' }
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }

    // Delete last chip on Backspace when input is empty
    if (e.key === 'Backspace' && input?.trim() === '' && list?.length > 0) {
      e.preventDefault(); // prevent text flicker
      setList((prev) => prev?.slice(0, -1)); // remove last keyword
    }
  };

  // ── Blur handler ──
  const handleBlur = () => {
    if (input?.trim()) {
      handleInputChange({
        target: { value: input + ',' }
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  // ── Delete handler ──
  const handleDelete = (kwToRemove: string) => {
    setList((prev) => prev.filter((kw) => kw !== kwToRemove));
  };

  // ── Convenience ──
  const clearError = () => {
    setError(null);
  };

  return {
    input,
    setInput,
    list,
    setList,
    error,
    setError,
    handleInputChange,
    handleKeyDown,
    handleBlur,
    handleDelete,
    clearError
  };
};
